/**
 * Service for managing school-defined date periods and date range configuration.
 * Supports arbitrary user-defined types (Term, Vacation, etc.) with a config doc
 * plus periods stored in the schoolDatePeriods subcollection.
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  runTransaction,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase.js';
import { ruleDate, schoolPeriodMidnight, schoolRuleTimeZone } from './domain/schoolRulePeriods';

const periodRows = snapshot => snapshot.docs.map(row => ({ ...row.data(), id: row.id }))
  .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
    || (ruleDate(a.createdAt)?.getTime() || 0) - (ruleDate(b.createdAt)?.getTime() || 0)
    || a.id.localeCompare(b.id));

class SchoolDatePeriodsService {
  constructor() {
    this.collectionName = 'schoolDatePeriods';
    this.configDocName = 'dateRangeConfig';
    this.cache = new Map();
    this.cacheTimestamp = new Map();
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
    this.listeners = new Map(); // schoolId -> unsubscribe fn
    this.configListeners = new Map();
  }

  // ─── Collection refs ────────────────────────────────────────────────

  getCollectionRef(schoolId) {
    return collection(db, 'schools', schoolId, this.collectionName);
  }

  getConfigRef(schoolId) {
    return doc(db, 'schools', schoolId, this.configDocName, 'config');
  }

  // ─── Cache helpers ──────────────────────────────────────────────────

  isCacheValid(schoolId) {
    const timestamp = this.cacheTimestamp.get(schoolId);
    if (!timestamp) return false;
    return Date.now() - timestamp < this.cacheDuration;
  }

  clearCache(schoolId = null) {
    if (schoolId) {
      this.cache.delete(schoolId);
      this.cacheTimestamp.delete(schoolId);
    } else {
      this.cache.clear();
      this.cacheTimestamp.clear();
    }
  }

  // ─── Config CRUD ────────────────────────────────────────────────────

  async getConfig(schoolId) {
    if (!schoolId) return null;
    try {
      const snap = await getDoc(this.getConfigRef(schoolId));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch (error) {
      console.error('Error fetching date range config:', error);
      throw error;
    }
  }

  async updateConfig(schoolId, data) {
    try {
      await setDoc(this.getConfigRef(schoolId), {
        ...data,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating date range config:', error);
      throw error;
    }
  }

  subscribeConfig(schoolId, callback) {
    if (!schoolId) { callback(null); return () => {}; }

    // Tear down previous listener
    const prev = this.configListeners.get(schoolId);
    if (prev) prev();

    const unsub = onSnapshot(this.getConfigRef(schoolId),
      (snap) => {
        callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      },
      (error) => {
        console.error('Config subscription error:', error);
        callback(null);
      }
    );

    this.configListeners.set(schoolId, unsub);
    return unsub;
  }

  // ─── Type helpers ───────────────────────────────────────────────────

  async createType(schoolId, typeData) {
    const config = await this.getConfig(schoolId) || {};
    const types = config.types || [];
    const maxSort = types.reduce((max, t) => Math.max(max, t.sortOrder || 0), 0);

    const newType = {
      id: typeData.id || typeData.name.toLowerCase().replace(/\s+/g, '-'),
      name: typeData.name,
      color: typeData.color || '#3B82F6',
      sortOrder: typeData.sortOrder || maxSort + 1000
    };

    await this.updateConfig(schoolId, {
      types: [...types, newType]
    });
    return newType;
  }

  async updateType(schoolId, typeId, updates) {
    const config = await this.getConfig(schoolId);
    if (!config?.types) return;

    const types = config.types.map(t =>
      t.id === typeId ? { ...t, ...updates } : t
    );
    await this.updateConfig(schoolId, { types });
  }

  async deleteType(schoolId, typeId) {
    const config = await this.getConfig(schoolId);
    if (!config?.types) return;

    // Remove from config
    const types = config.types.filter(t => t.id !== typeId);
    await this.updateConfig(schoolId, { types });

    // Cascade-delete all periods of this type
    const periods = await this.getAll(schoolId);
    const toDelete = periods.filter(p => p.type === typeId || p.typeId === typeId);

    if (toDelete.length > 0) {
      const batch = writeBatch(db);
      toDelete.forEach(p => {
        batch.delete(doc(this.getCollectionRef(schoolId), p.id));
      });
      await batch.commit();
      this.clearCache(schoolId);
    }
  }

  // ─── Seed default config ───────────────────────────────────────────

  async seedDefaultConfig(schoolId) {
    const existing = await this.getConfig(schoolId);
    if (existing?.types?.length) return existing; // already set up

    const now = new Date();
    const year = now.getFullYear();

    const defaultConfig = {
      academicYear: {
        year,
        startDate: Timestamp.fromDate(new Date(year, 0, 1)),
        endDate: Timestamp.fromDate(new Date(year, 11, 31))
      },
      types: [
        { id: 'year', name: 'Year', color: '#10B981', sortOrder: 500 },
        { id: 'term', name: 'Term', color: '#3B82F6', sortOrder: 1000 },
        { id: 'semester', name: 'Semester', color: '#8B5CF6', sortOrder: 2000 }
      ],
      createdAt: serverTimestamp()
    };

    await this.updateConfig(schoolId, defaultConfig);
    return defaultConfig;
  }

  // ─── Real-time subscription for periods ─────────────────────────────

  subscribe(schoolId, callback) {
    if (!schoolId) { callback([]); return () => {}; }

    // Tear down previous listener
    const prev = this.listeners.get(schoolId);
    if (prev) prev();

    // Sorting in the editor must not hide legacy saved dates without sort metadata.
    const unsub = onSnapshot(this.getCollectionRef(schoolId),
      (snapshot) => {
        const periods = periodRows(snapshot);
        // Update cache while we're at it
        this.cache.set(schoolId, periods);
        this.cacheTimestamp.set(schoolId, Date.now());
        callback(periods);
      },
      (error) => {
        console.error('Periods subscription error:', error);
        callback([]);
      }
    );

    this.listeners.set(schoolId, unsub);
    return unsub;
  }

  unsubscribe(schoolId) {
    const unsub = this.listeners.get(schoolId);
    if (unsub) { unsub(); this.listeners.delete(schoolId); }

    const configUnsub = this.configListeners.get(schoolId);
    if (configUnsub) { configUnsub(); this.configListeners.delete(schoolId); }
  }

  // ─── Periods CRUD (existing, enhanced) ──────────────────────────────

  async updateCalendarDates(schoolId, periodId, updates, expectedTimeZone) {
    const periodRef = doc(this.getCollectionRef(schoolId), periodId);
    await runTransaction(db, async transaction => {
      const school = await transaction.get(doc(db, 'schools', schoolId));
      if (!school.exists()) throw new Error('The school is unavailable. Reload Time Periods before saving.');
      const timeZone = schoolRuleTimeZone(school.data());
      if (timeZone !== expectedTimeZone) {
        throw new Error('The school timezone changed. Reload Time Periods before saving these dates.');
      }
      const patch = { ...updates, updatedAt: serverTimestamp() };
      for (const field of ['startDate', 'endDate']) {
        if (!Object.prototype.hasOwnProperty.call(updates, field)) continue;
        const date = schoolPeriodMidnight(updates[field], timeZone);
        patch[field] = date ? Timestamp.fromDate(date) : null;
      }
      transaction.update(periodRef, patch);
    });
    this.clearCache(schoolId);
  }

  async getAll(schoolId) {
    if (!schoolId) return [];

    if (this.isCacheValid(schoolId) && this.cache.has(schoolId)) {
      return this.cache.get(schoolId);
    }

    try {
      const snapshot = await getDocs(this.getCollectionRef(schoolId));
      const periods = periodRows(snapshot);

      this.cache.set(schoolId, periods);
      this.cacheTimestamp.set(schoolId, Date.now());

      return periods;
    } catch (error) {
      console.error('Error fetching date periods:', error);
      throw error;
    }
  }

  async getActivePeriods(schoolId) {
    const allPeriods = await this.getAll(schoolId);
    return allPeriods.filter(period => period.isActive !== false);
  }

  async getActivePeriod(schoolId, type) {
    const activePeriods = await this.getActivePeriods(schoolId);
    const now = new Date();

    return activePeriods.find(period => {
      if (period.type !== type) return false;

      const startDate = period.startDate?.toDate ? period.startDate.toDate() : new Date(period.startDate);
      const endDate = period.endDate?.toDate ? period.endDate.toDate() : new Date(period.endDate);

      return now >= startDate && now <= endDate;
    }) || null;
  }

  async create(schoolId, periodData) {
    try {
      const typeId = periodData.typeId || periodData.type;

      const docData = {
        ...periodData,
        type: typeId,       // backward compat
        typeId: typeId,     // canonical
        schoolId,
        isActive: periodData.isActive !== undefined ? periodData.isActive : true,
        sortOrder: periodData.sortOrder || 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(this.getCollectionRef(schoolId), docData);
      this.clearCache(schoolId);
      return docRef.id;
    } catch (error) {
      console.error('Error creating date period:', error);
      throw error;
    }
  }

  async update(schoolId, periodId, periodData) {
    try {
      // Keep type and typeId in sync
      const updates = { ...periodData };
      if (updates.typeId && !updates.type) updates.type = updates.typeId;
      if (updates.type && !updates.typeId) updates.typeId = updates.type;

      const docRef = doc(this.getCollectionRef(schoolId), periodId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      this.clearCache(schoolId);
    } catch (error) {
      console.error('Error updating date period:', error);
      throw error;
    }
  }

  async delete(schoolId, periodId) {
    try {
      const docRef = doc(this.getCollectionRef(schoolId), periodId);
      await deleteDoc(docRef);
      this.clearCache(schoolId);
    } catch (error) {
      console.error('Error deleting date period:', error);
      throw error;
    }
  }

  // ─── Helpers ────────────────────────────────────────────────────────

  calculateDuration(startDate, endDate) {
    const start = startDate?.toDate ? startDate.toDate() : new Date(startDate);
    const end = endDate?.toDate ? endDate.toDate() : new Date(endDate);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  isCurrentPeriod(period) {
    if (!period?.startDate || !period?.endDate) return false;
    const now = new Date();
    const start = period.startDate?.toDate ? period.startDate.toDate() : new Date(period.startDate);
    const end = period.endDate?.toDate ? period.endDate.toDate() : new Date(period.endDate);
    return now >= start && now <= end;
  }
}

// Export singleton instance
export const schoolDatePeriodsService = new SchoolDatePeriodsService();
export default schoolDatePeriodsService;
